import {
    animate,
    state,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import {
    TelXLAgentDirectoryPanelComponent,
    TelXLContactDirectoryPanelComponent,
    TelXLCustomerDirectoryPanelComponent,
    TelXLInternalDirectoryPanelComponent,
    TelXLIvrDirectoryPanelComponent,
} from '@dxp/components';
import { TelXLTabComponent } from '@dxp/elements';
import { recalculateHeight } from '@dxp/core/helpers';
import {
    IAgentDirectoryEvent,
    IBlenderAgent,
    IContact,
    IContactDirectoryEvent,
    IContactInternalDirectoryEvent,
    IContactSelected,
    ICustomerDetails,
    ICustomerDirectoryEvent,
    IIvrDirectoryEvent,
    ITab,
    IUser,
} from '@dxp/core/models';
import { VoiceService } from '@dxp/core/services';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { VoiceActions } from '@dxp/core/types';
import { CONTACT_STORE, USER_STORE } from '@dxp/core/stores';
import { VoiceCallNewService } from '../voice/services/voice-call-new.service';
import { VoiceStore } from '../voice/store/voice.store';
import { VoiceContactActionComponent } from './action/contacts-action.component';

type Options = 'agents' | 'customers' | 'contacts' | 'internal' | 'ivr';

interface ISelection {
    type: Options;
    id: string | number;
    object: IContact | ICustomerDetails | IBlenderAgent;
}

@Component({
    selector: 'voice-contacts-directory',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        VoiceContactActionComponent,
        TelXLTabComponent,
        TelXLCustomerDirectoryPanelComponent,
        TelXLContactDirectoryPanelComponent,
        TelXLAgentDirectoryPanelComponent,
        TelXLInternalDirectoryPanelComponent,
        TelXLIvrDirectoryPanelComponent,
    ],
    templateUrl: './contact.component.html',
    styleUrl: './contact.component.scss',
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: 0 })),
            transition(':enter', [
                animate('250ms ease-in', style({ opacity: 1 })),
            ]),
        ]),
    ],
})
export class VoiceContactComponent implements OnInit {
    @ViewChild('contactsaction') contactsAction!: VoiceContactActionComponent;

    @ViewChild(TelXLContactDirectoryPanelComponent)
    private contactDirectoryPanel!: TelXLContactDirectoryPanelComponent;

    @ViewChild(TelXLCustomerDirectoryPanelComponent)
    private customerDirectoryPanel!: TelXLCustomerDirectoryPanelComponent;

    readonly #voiceService = inject(VoiceService);
    readonly #voiceCallNewService = inject(VoiceCallNewService);
    readonly #userStore = inject(USER_STORE);
    readonly #voiceStore = inject(VoiceStore);

    #contactStore = inject(CONTACT_STORE);

    state = input<{
        title: string;
        action: VoiceActions;
        close: boolean;
    }>({
        title: '',
        action: 'voice-new',
        close: false,
    });
    conversationId = input<string>();
    workItemId = input<string>();
    selected = output<IContactSelected>();
    closed = output<boolean>();

    readonly actionLoaded = signal<boolean>(false);
    readonly disabled = signal<boolean>(false);
    readonly hidden = computed(() => this.#voiceService.contacts().hidden);
    readonly selection = signal<ISelection | null>(null);

    readonly agent = computed<IUser>(() => this.#userStore.user());

    readonly showCallNumber = computed<boolean>(
        () =>
            this.state().action === 'voice-transfer' ||
            this.state().action === 'voice-enquiry',
    );
    readonly canCall = computed<boolean>(
        () => this.callNumber().trim().length > 0,
    );
    readonly callNumber = signal<string>('');

    readonly defaultTabByAction: Record<VoiceActions, Options> = {
        'voice-new': 'agents',
        'voice-transfer': 'agents',
        'voice-enquiry': 'agents',
        'voice-ivr': 'ivr',
    };

    readonly #tabSelected = signal<Options | undefined>(undefined);
    readonly #selected = computed<Options>(
        () =>
            this.#tabSelected() ??
            this.defaultTabByAction[this.state().action] ??
            this.#tabPermissions[this.state().action][0],
    );

    readonly canClose = computed(
        () => !this.#voiceService.enquiryCallDisplayed(),
    );

    readonly tabs = computed<ITab[]>(() => [
        { label: 'Agents', index: 'agents', disabled: this.disabled() },
        {
            label: 'External Directory',
            index: 'contacts',
            disabled: this.disabled(),
        },
        {
            label: 'End Customers',
            index: 'customers',
            disabled: this.disabled(),
        },
        {
            label: 'Internal Directory',
            index: 'internal',
            disabled: this.disabled(),
        },
        { label: 'IVRs', index: 'ivr', disabled: this.disabled() },
    ]);

    readonly #tabPermissions: Record<VoiceActions, Options[]> = {
        'voice-new': ['contacts', 'customers', 'agents', 'internal'],
        'voice-transfer': ['agents', 'internal', 'contacts'],
        'voice-enquiry': ['agents', 'internal', 'contacts'],
        'voice-ivr': ['ivr'],
    };

    filteredTabs = computed<ITab[]>(() => {
        const allowed = this.#tabPermissions[this.state().action] ?? [];
        return this.tabs().filter(tab =>
            allowed.includes(tab.index as Options),
        );
    });

    get tabSelected() {
        return this.#selected();
    }

    set tabSelected(value: Options) {
        this.#tabSelected.set(value);
    }

    contactsHeight = computed<string>(() => {
        let offset = '2rem';
        if (this.showCallNumber()) offset = '6.375rem';
        if (this.state().close) offset = '4.6875rem';
        if (this.state().close && this.showCallNumber()) offset = '9.0625rem';
        if (this.state().close && this.actionLoaded()) offset = '22.875rem';

        return recalculateHeight('communication', 'standard', offset);
    });

    /**
     * Map to an observable to get the correct call state
     */
    outboundCallState$ = toObservable(this.#voiceStore.outboundCallState).pipe(
        takeUntilDestroyed(),
    );

    agentUnavailable$ = toObservable(
        this.#voiceCallNewService.agentUnavailable,
    ).pipe(takeUntilDestroyed());

    /**
     * Listen to the selected tab
     */
    selectedTab$ = toObservable(this.#selected).pipe(takeUntilDestroyed());

    ngOnInit(): void {
        this.agentUnavailable$.subscribe(unavailable => {
            if (unavailable) this.disabled.set(false);
        });

        this.outboundCallState$.subscribe(callState => {
            /**
             * Disable as soon as the button is pressed
             * For both directories
             */
            if (
                this.#selected() === 'agents' ||
                this.#selected() === 'internal'
            ) {
                /**
                 * List to the following states to disable the button
                 */
                if (
                    this.disabled() &&
                    (callState === 'Rejected' ||
                        callState === 'Failed' ||
                        callState === 'Connected')
                ) {
                    this.disabled.set(false);
                }
            }
        });

        /**
         * Subscribe to the selected Tab
         */
        this.selectedTab$.subscribe(selectedTab => {
            this.#contactStore.updateActiveTab(selectedTab);
        });
    }

    height = computed<string>(() =>
        recalculateHeight('communication', 'standard', '2rem'),
    );

    setSelected(value: string) {
        this.tabSelected = value as Options;
    }

    contactSelected(event: IContactDirectoryEvent) {
        this.loadActionComponent('c' + event.item.id, {
            name: event.item.fullName,
            identifier: event.item.phoneNumber,
            type: 'contact',
        });
    }

    customerSelected(event: ICustomerDirectoryEvent) {
        this.loadActionComponent('cu' + event.item.customerId, {
            name: event.item.name ?? '',
            identifier: event.item.phoneNumbers?.[0].number ?? '',
            type: 'contact',
        });
    }

    agentSelected(event: IAgentDirectoryEvent) {
        this.loadActionComponent(
            'a' + event.item.id,
            {
                name: event.item.name,
                identifier: event.item.phoneNumber,
                type: 'agent',
                internalCall: true,
            },
            true,
        );
    }

    internalSelected(event: IContactInternalDirectoryEvent) {
        this.loadActionComponent(
            'a' + event.item.id,
            {
                name: event.item.displayName ?? '',
                identifier: event.item.phoneNumber,
                type: 'contact',
                internalCall: true,
            },
            true,
        );
    }

    ivrSelected(event: IIvrDirectoryEvent) {
        this.loadActionComponent('i' + event.item.ID, {
            name: event.item.title ?? '',
            identifier: event.item.ID.toString(),
            type: 'ivr',
        });
    }

    onContactUpdated(contact: IContact) {
        this.#voiceService.setContactCardSelected('c' + contact.id);
        this.contactDirectoryPanel.refresh(contact);
    }

    onCustomerUpdated(customer: ICustomerDetails) {
        this.#voiceService.setContactCardSelected('cu' + customer.customerId);
        this.customerDirectoryPanel.refresh(customer);
    }

    onCallNumberChange(event: Event) {
        const inputValue = (event.target as HTMLInputElement).value;
        this.callNumber.set(inputValue);
    }

    get calculateHeight(): string {
        return recalculateHeight('contacts', 'standard');
    }

    get customerSelection(): string | undefined {
        const selection = this.#voiceService.getContactCardSelected();
        return selection.startsWith('cu') ? selection.slice(2) : undefined;
    }

    get contactSelection(): number | undefined {
        const selection = this.#voiceService.getContactCardSelected();
        return !selection.startsWith('cu') && selection.startsWith('c')
            ? parseInt(selection.slice(1) as string)
            : undefined;
    }

    get agentSelection(): string | undefined {
        const selection = this.#voiceService.getContactCardSelected();
        return selection.startsWith('a') ? selection.slice(1) : undefined;
    }

    get internalSelection(): string | undefined {
        const selection = this.#voiceService.getContactCardSelected();
        return selection.startsWith('a') ? selection.slice(1) : undefined;
    }

    get ivrSelection(): string | undefined {
        const selection = this.#voiceService.getContactCardSelected();
        return selection.startsWith('i') ? selection.slice(1) : undefined;
    }

    loadActionComponentFromNumber() {
        const contact: IContactSelected = {
            name: 'Dialled Number',
            identifier: this.callNumber(),
            type: 'number',
        };

        this.contactsAction.loadComponent(
            this.state().action,
            this.conversationId() as string,
            this.workItemId() as string,
            contact,
        );
        this.actionLoaded.set(true);
        this.disabled.set(true);
    }

    private loadActionComponent(
        id: string,
        contact: IContactSelected,
        makeInternalCall = false,
    ) {
        /**
         * Disable as soon as the button is pressed
         * If we are on either one of these two tabs
         */
        if (this.#selected() === 'agents' || this.#selected() === 'internal') {
            this.disabled.set(true);
        }

        if (this.state().action !== 'voice-new') {
            this.contactsAction.loadComponent(
                this.state().action,
                this.conversationId() as string,
                this.workItemId() as string,
                contact,
            );
            makeInternalCall = false;
            this.actionLoaded.set(true);
            this.disabled.set(true);
        } else {
            this.selected.emit(contact);
        }

        this.#voiceService.setContactCardSelected(id);

        if (makeInternalCall) {
            this.#voiceCallNewService.internalCostCentreChange();
            this.#voiceCallNewService.makeCall(contact.type);
        } else {
            this.#voiceCallNewService.resetInternalCostCentre();
        }
    }
}
